import { useNavigate } from "react-router-dom";
import { ArrowRight, Recycle, ShieldCheck, Trash2, Truck, PoundSterling, Bell, FileCheck, Languages } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";

// On iOS 26 WebViews we've seen `<Link>` wrapping a `<Button>` occasionally
// bubble through as a raw anchor navigation to `capacitor://localhost/…`
// which iOS can't resolve — the WebView backgrounds itself and the user
// lands on the Home Screen. Using `useNavigate()` + a plain onClick keeps
// the router in control and avoids emitting an anchor tag altogether.
export default function Landing() {
  const navigate = useNavigate();
  const go = (path) => () => navigate(path);
  return (
    <Layout>
      {/* HERO — mobile-first: image first on phones, content stacked tight; switches to
          two-column at lg+ for tablet/desktop. */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 sm:pt-10 lg:pt-14">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          {/* Image column — shown first on mobile (short aspect) and second on lg+ */}
          <div className="order-1 lg:order-2 lg:col-span-5 fade-up delay-1">
            <div className="relative rounded-3xl overflow-hidden border border-[#E2DFD8] aspect-[16/10] sm:aspect-[4/3] lg:aspect-[4/5] bg-[#F6F4F0]">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/4677e286-7ef3-44e1-af88-24eec128e7e7/images/ea09d962195c00e97ac1601d46f641bfaac607fe789390293a10d5a98da90a3f.png"
                alt="Binshare"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B3624]/35 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 sm:bottom-5 sm:left-5 sm:right-5 bg-white/95 backdrop-blur-sm rounded-2xl p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] sm:text-xs text-[#6B6965] uppercase tracking-wide">Live bid</div>
                    <div className="text-sm sm:text-base font-medium" style={{ fontFamily: "Outfit" }}>Garden waste · Manchester</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl sm:text-2xl font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>£28.50</div>
                    <div className="text-[10px] sm:text-xs text-[#4A6B56]">GreenWheels</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content column */}
          <div className="order-2 lg:order-1 lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 bg-[#EBE8E0] text-[#1B3624] px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold tracking-wide uppercase">
              <Recycle className="w-3.5 h-3.5" />
              UK waste clearance, on demand
            </div>
            <h1
              className="mt-4 sm:mt-5 text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.08]"
              style={{ fontFamily: "Outfit" }}
              data-testid="hero-title"
            >
              Snap a photo.
              <span className="text-[#4A6B56]"> Get it gone.</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg text-[#6B6965] leading-relaxed max-w-xl">
              Something you no longer need? Choose the right route — <span className="text-[#1B3624] font-medium">Reuse</span> it for free,
              <span className="text-[#1B3624] font-medium"> Remove</span> a one-off item, or <span className="text-[#1B3624] font-medium">Clear</span> a whole space with a licensed pro.
            </p>
            <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
              <Card
                role="button"
                tabIndex={0}
                data-testid="route-reuse"
                onClick={() => navigate("/reuse")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/reuse"); } }}
                className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
              >
                <Recycle className="w-6 h-6 text-[#4A6B56]" />
                <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>Reuse</div>
                <div className="text-xs text-[#6B6965] mt-1">Give it away, free. A neighbour collects.</div>
              </Card>
              <Card
                role="button"
                tabIndex={0}
                data-testid="route-remove"
                onClick={() => navigate("/post-waste")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/post-waste"); } }}
                className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
              >
                <Trash2 className="w-6 h-6 text-[#D67B54]" />
                <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>Remove</div>
                <div className="text-xs text-[#6B6965] mt-1">One-off item. Licensed carrier collects.</div>
              </Card>
              <Card
                role="button"
                tabIndex={0}
                data-testid="route-clear"
                onClick={() => navigate("/post-waste")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/post-waste"); } }}
                className="p-5 rounded-2xl border-[#E2DFD8] bg-white hover:border-[#4A6B56] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
              >
                <Truck className="w-6 h-6 text-[#1B3624]" />
                <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>Clear</div>
                <div className="text-xs text-[#6B6965] mt-1">Whole room, garden or tenancy — one pro job.</div>
              </Card>
            </div>
            <div className="mt-6 sm:mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-[#6B6965]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#4A6B56]" /> Licensed carriers only</div>
              <div className="flex items-center gap-2"><FileCheck className="w-4 h-4 text-[#4A6B56]" /> Duty of care included</div>
              <div className="flex items-center gap-2"><PoundSterling className="w-4 h-4 text-[#4A6B56]" /> Pay-as-you-go</div>
            </div>
            {/* Live store links — App Store + Google Play */}
            <div className="mt-6 flex flex-wrap gap-3" data-testid="landing-store-links">
              <a
                href="https://apps.apple.com/gb/app/bin-share-app/id6766317157"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="store-link-appstore"
                aria-label="Download Binshare on the App Store"
                className="inline-flex"
              >
                <img
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-gb"
                  alt="Download on the App Store"
                  className="h-11 w-auto"
                  loading="lazy"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=uk.co.binshare.marketplace"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="store-link-playstore"
                aria-label="Get Binshare on Google Play"
                className="inline-flex"
              >
                <img
                  src="https://play.google.com/intl/en_gb/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-11 w-auto"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* £500 Flytipping Guarantee trust band */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 sm:mt-20">
        <Card
          data-testid="protection-badge"
          className="rounded-3xl border-[#1B3624] bg-[#1B3624] text-white p-6 sm:p-10 flex flex-wrap gap-6 items-center justify-between"
        >
          <div className="flex items-start gap-4 min-w-[240px]">
            <div className="w-12 h-12 rounded-2xl bg-[#D67B54] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#D67B54] font-semibold">£500 Flytipping Guarantee</div>
              <h3 className="mt-1 text-xl sm:text-2xl font-medium" style={{ fontFamily: "Outfit" }}>
                Fly-tipping insurance, on the house.
              </h3>
              <p className="mt-2 text-sm text-white/80">
                Every carrier holds a verified Environment Agency waste-carrier licence. If your waste is illegally
                disposed of via a Binshare job, we'll cover your costs up to <strong>£500</strong>.
                Subject to our <a href="/policies" className="underline hover:text-white">terms</a>.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Bento grid features */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 sm:mt-20">
        <div className="grid md:grid-cols-12 gap-4 sm:gap-5">
          <div className="md:col-span-8 bg-[#F6F4F0] border border-[#E2DFD8] rounded-3xl p-6 sm:p-8 md:p-10 bento-texture fade-up delay-2">
            <Trash2 className="w-6 h-6 sm:w-7 sm:h-7 text-[#4A6B56]" />
            <h2 className="mt-3 sm:mt-4 text-xl sm:text-2xl md:text-3xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
              Clear waste in 30 seconds
            </h2>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-[#6B6965] max-w-lg">
              Snap a photo, describe what you've got, drop a pin. Licensed UK carriers race to bid — you pick the best price. Check the price guide to see what jobs typically cost. Junk removal, house clearance, garden waste, trade waste — all in one app.
            </p>
            <Button data-testid="feat-post-btn" onClick={go("/post-waste")} variant="ghost" className="mt-4 sm:mt-5 rounded-full text-[#1B3624] hover:bg-[#EBE8E0] px-0 hover:px-4">
              Snap & quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="md:col-span-4 bg-[#1B3624] text-white rounded-3xl p-6 sm:p-8 fade-up delay-3">
            <Truck className="w-6 h-6 sm:w-7 sm:h-7 text-[#D67B54]" />
            <h3 className="mt-3 sm:mt-4 text-lg sm:text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
              Carriers: find local leads
            </h3>
            <p className="mt-2 sm:mt-3 text-white/75 text-xs sm:text-sm leading-relaxed">
              Independent licensed carriers browse live jobs and submit competitive bids. Set your own prices — get paid via Stripe the moment a customer accepts your bid.
            </p>
          </div>

          <div className="md:col-span-4 bg-[#F6F4F0] border border-[#E2DFD8] rounded-3xl p-6 sm:p-8 fade-up delay-3 overflow-hidden">
            <Recycle className="w-6 h-6 sm:w-7 sm:h-7 text-[#4A6B56] mb-3" />
            <div className="inline-block text-[10px] uppercase tracking-wider text-[#6B6965] bg-white border border-[#E2DFD8] rounded-full px-2 py-0.5 mb-2">New</div>
            <h3 className="text-lg sm:text-xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
              Free stuff, on Binshare
            </h3>
            <p className="mt-1.5 sm:mt-2 text-[#6B6965] text-xs sm:text-sm">Still usable? List it for free — a neighbour collects it and keeps it out of landfill.</p>
            <Button data-testid="feat-reuse-btn" onClick={go("/reuse")} variant="ghost" className="mt-4 rounded-full text-[#1B3624] hover:bg-[#EBE8E0] px-0 hover:px-4">
              Browse Free stuff <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="md:col-span-8 bg-[#F6F4F0] border border-[#E2DFD8] rounded-3xl p-6 sm:p-8 md:p-10 fade-up delay-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <Feature icon={<FileCheck className="w-5 h-5 sm:w-6 sm:h-6" />} title="Duty of care" body="Receive a duty-of-care receipt after every completed job — your proof of legal disposal." />
              <Feature icon={<ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />} title="Verified" body="Only licensed waste carriers can bid for jobs." />
              <Feature icon={<Bell className="w-5 h-5 sm:w-6 sm:h-6" />} title="Notified" body="Real-time alerts the moment your bid lands." />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 sm:mt-20 mb-10 sm:mb-12">
        <div className="rounded-3xl bg-[#1B3624] text-white p-6 sm:p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6">
          <div>
            <div
              data-testid="welsh-language-badge"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/90 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium tracking-wide mb-4"
            >
              <Languages className="w-3.5 h-3.5" />
              Available in English and Welsh / Ar gael yn Gymraeg a Saesneg
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium max-w-xl" style={{ fontFamily: "Outfit" }}>
              Join the UK's waste removal marketplace
            </h2>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/75 max-w-md">Pick your role and start posting, bidding, or reusing in minutes.</p>
          </div>
          <Button
            data-testid="cta-join"
            onClick={go("/login")}
            className="w-full md:w-auto rounded-full bg-[#D67B54] hover:bg-[#c66c44] text-white px-7 py-5 sm:py-6 text-base"
          >
            Join Binshare <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>
    </Layout>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div>
      <div className="w-11 h-11 rounded-xl bg-[#EBE8E0] flex items-center justify-center text-[#1B3624]">{icon}</div>
      <div className="mt-3 font-medium" style={{ fontFamily: "Outfit" }}>{title}</div>
      <div className="mt-1 text-sm text-[#6B6965] leading-relaxed">{body}</div>
    </div>
  );
}
